import { lazy, Suspense } from 'react';
import { Routes, Route } from "react-router-dom";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { ProtectedLayout } from "@/components/layout/ProtectedLayout";
import { retryLazy } from "@/utils/retry-lazy";
import { ErrorBoundary } from "@/components/error/ErrorBoundary";
import { Loader2 } from "lucide-react";
import * as Sentry from "@sentry/react";

// Lazy load components with retry logic and error tracking
const lazyWithRetry = (importFn: () => Promise<any>) => {
  return Sentry.withProfiler(lazy(() => 
    retryLazy(importFn).catch(error => {
      Sentry.captureException(error);
      throw error;
    })
  ));
};

// Lazy load components with retry logic
const Login = lazyWithRetry(() => import("@/pages/Login"));
const VerifyMFA = lazyWithRetry(() => import("@/pages/VerifyMFA"));
const Register = lazyWithRetry(() => import("@/pages/Register"));
const UpdatePassword = lazyWithRetry(() => import("@/pages/UpdatePassword"));
const EmailConfirmed = lazyWithRetry(() => import("@/pages/EmailConfirmed"));
const CheckEmail = lazyWithRetry(() => import("@/pages/CheckEmail"));
const Tests = lazyWithRetry(() => import("@/pages/Tests"));
const TakeTest = lazyWithRetry(() => import("@/pages/TakeTest"));
const HealthProfile = lazyWithRetry(() => import("@/pages/HealthProfile"));
const Settings = lazyWithRetry(() => import("@/pages/Settings"));
const Terms = lazyWithRetry(() => import("@/pages/Terms"));
const Dashboard = lazyWithRetry(() => import("@/pages/Dashboard"));
const PrivacyPolicy = lazyWithRetry(() => import("@/pages/PrivacyPolicy"));
const Disclaimer = lazyWithRetry(() => import("@/pages/Disclaimer"));
const Index = lazyWithRetry(() => import("@/pages/Index"));
const Results = lazyWithRetry(() => import("@/pages/Results"));
const FAQs = lazyWithRetry(() => import("@/pages/FAQs"));
const Help = lazyWithRetry(() => import("@/pages/Help"));
const NotFound = lazyWithRetry(() => import("@/pages/NotFound"));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin" />
  </div>
);

export const AppRoutes = () => (
  <ErrorBoundary>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Login />
          </Suspense>
        </PublicLayout>
      } />
      <Route path="/verify-mfa" element={
        <PublicLayout>
          <Suspense fallback={<LoadingFallback />}>
            <VerifyMFA />
          </Suspense>
        </PublicLayout>
      } />
      <Route path="/register" element={
        <PublicLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Register />
          </Suspense>
        </PublicLayout>
      } />
      <Route path="/check-email" element={
        <PublicLayout>
          <Suspense fallback={<LoadingFallback />}>
            <CheckEmail />
          </Suspense>
        </PublicLayout>
      } />
      <Route path="/update-password" element={
        <PublicLayout>
          <Suspense fallback={<LoadingFallback />}>
            <UpdatePassword />
          </Suspense>
        </PublicLayout>
      } />
      <Route path="/email-confirmed" element={
        <PublicLayout>
          <Suspense fallback={<LoadingFallback />}>
            <EmailConfirmed />
          </Suspense>
        </PublicLayout>
      } />
      <Route path="/terms" element={
        <PublicLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Terms />
          </Suspense>
        </PublicLayout>
      } />
      <Route path="/privacy-policy" element={
        <PublicLayout>
          <Suspense fallback={<LoadingFallback />}>
            <PrivacyPolicy />
          </Suspense>
        </PublicLayout>
      } />
      <Route path="/disclaimer" element={
        <PublicLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Disclaimer />
          </Suspense>
        </PublicLayout>
      } />
      <Route path="/" element={
        <PublicLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Index />
          </Suspense>
        </PublicLayout>
      } />

      {/* Protected routes */}
      <Route path="/results/:resultId" element={
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Results />
          </Suspense>
        </ProtectedLayout>
      } />
      <Route path="/dashboard" element={
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Dashboard />
          </Suspense>
        </ProtectedLayout>
      } />
      <Route path="/tests" element={
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Tests />
          </Suspense>
        </ProtectedLayout>
      } />
      <Route path="/test/:testId" element={
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}>
            <TakeTest />
          </Suspense>
        </ProtectedLayout>
      } />
      <Route path="/health-profile/:resultId?" element={
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}>
            <HealthProfile />
          </Suspense>
        </ProtectedLayout>
      } />
      <Route path="/settings" element={
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Settings />
          </Suspense>
        </ProtectedLayout>
      } />
      <Route path="/faqs" element={
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}>
            <FAQs />
          </Suspense>
        </ProtectedLayout>
      } />
      <Route path="/help" element={
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Help />
          </Suspense>
        </ProtectedLayout>
      } />

      {/* Error and 404 routes */}
      <Route path="*" element={
        <Suspense fallback={<LoadingFallback />}>
          <NotFound />
        </Suspense>
      } />
    </Routes>
  </ErrorBoundary>
);